import {announce} from '@github-ui/aria-live'
import {FileAttachment} from '@github-ui/file-attachment'
import type {Attachment} from '@github-ui/file-attachment/attachment'
import type {AnchorSide} from '@primer/behaviors'
import {UploadIcon} from '@primer/octicons-react'
import {type ReactNode, type RefObject, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'

import type {
  AhpSpaceSelection,
  AhpSubmissionMetadata,
  ContributedInstructionMetadata,
  ContributedInstructionsMetadata,
  EstablishedAhpSession,
  FirstTurnHandoff,
  PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import type {EstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'
import type {TaskStartup, TaskStartupLaunch} from '@github-ui/agent-sessions/ahp/task-startup-store'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import {useEstablishedAhpComposerFocusReturn} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-composer-focus-return'
import {AppliedInstructionsDialog} from '@github-ui/copilot-chat/components/AppliedInstructionsDialog'
import {PersonalInstructionsDialog} from '@github-ui/copilot-chat/components/PersonalInstructionsDialog'

import styles from '../ChatApp.module.css'
import {
  useHasCopilotChatManager,
  useOptionalContributedInstructionSnapshot as useLegacyContributedInstructionSnapshot,
} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {
  useHasCustomInstructionsProvider,
  useOptionalContributedInstructionSnapshot,
} from '../custom-instructions/CustomInstructionsProvider'
import {useOptionalPersonalInstructionsEditor} from '@github-ui/copilot-shared-ui/contexts/ContributedInstructionsContext'
import {useConversationHeaderState} from '../providers/use-conversation-header-state'
import {
  useNativeAhpSession,
  useNativeAhpTimelineFallbackState,
  useNativeAhpTimelineState,
  useModePicker,
  useProvisioningKind,
  useRuntimeStatus,
} from '../providers/use-timeline-contexts'
import type {ConversationDeletion} from '../runtime/use-conversation-deleted-navigation'
import type {SubmissionFailure} from '@github-ui/ahp-session-client/submission-error'
import type {AhpSessionIdentifiers, ChatDisplayIdentity} from '../types'

import {AhpSessionComposer, type ComposerHandle, type EmptyStateMode} from '@github-ui/one-copilot-composer/existing'
import {DeleteConversationDialogContainer} from './DeleteConversationDialogContainer'
import {QuickChatHeader, QUICK_CHAT_HEADING_ID} from './QuickChatHeader'
import {EnvironmentSetupLazy} from './EnvironmentSetup/EnvironmentSetupLazy'
import {useEnvironmentSetupVisibility} from './EnvironmentSetup/use-environment-setup-visibility'
import {ShipFeedbackBanner} from '@github-ui/copilot-shared-ui/components/ShipFeedbackBanner'
import {Timeline, type TimelineAutoScrollHandle} from './Timeline'
import {useOptimisticRecoveryTurns} from '@github-ui/copilot-shared-ui/hooks/use-optimistic-recovery-turns'
import {Artifacts} from '@github-ui/agent-sessions/components/artifacts/SessionArtifacts'
import {
  ChatChangesetActions,
  EstablishedTaskInputActions,
  PersistedTaskInputActions,
} from '@github-ui/agent-sessions/components/task-view/EstablishedTaskChatInputActions'
import {DiffOverlay} from '@github-ui/agent-sessions/components/task-view/DiffOverlay'
import {deriveArtifactsFromChatState} from '@github-ui/agent-sessions/utils/artifacts/derive-artifacts-from-chat-state'
import {CLIModePicker} from '@github-ui/agent-sessions/components/task-view/CLIModePicker'
import {useEstablishedApprovalModeMutation} from '@github-ui/agent-sessions/hooks/use-established-approval-mode-mutation'
import {usePermissionCommandHandler} from '@github-ui/agent-sessions/hooks/use-permission-command-handler'
import {
  taskHasRepository,
  useRepositoryCommandGuard,
} from '@github-ui/agent-sessions/hooks/use-repository-command-guard'
import {fetchTask} from '@github-ui/agent-sessions/hooks/use-task'
import {useQueryClient} from '@github-ui/react-query'
import {useShareCommandHandler} from '@github-ui/agent-sessions/hooks/use-share-command-handler'
import {useStaffDebugFeedbackToast} from '@github-ui/agent-sessions/hooks/use-staff-debug-feedback-toast'
import {useChatEntitlementOnTurnSettlement} from './use-chat-entitlement-on-turn-settlement'
import {DEFAULT_AGENT_MODE} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {TaskAhpHistory} from '@github-ui/agent-sessions/hooks/use-task-ahp-history'
import {SessionResumeNotice} from '@github-ui/agent-sessions/components/task-view/SessionResumeNotice'
import {SessionLoadingIndicator} from '@github-ui/agent-sessions/components/session-loading/SessionLoadingIndicator'
import {
  SessionLoadingBoundary,
  useSessionLoadingPresentation,
  useSessionLoadingSource,
  type SessionLoadingSource,
} from '@github-ui/agent-sessions/hooks/use-session-loading-presentation'
import type {RelayConnectionProgress} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {RelayFailure} from '@github-ui/ahp-relay/relay-failure'
import type {SessionResumeErrorEntrypoint} from '@github-ui/agent-sessions/utils/session-resume-error-metric'
import type {SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'

const ENVIRONMENT_READY_ANNOUNCEMENT = 'Connected.'

export interface ChatShellLayoutContent {
  header: ReactNode
  content: ReactNode
}

export type ChatShellLayoutRenderer = (content: ChatShellLayoutContent) => ReactNode

interface SessionResumeState {
  /** Launch path reported with session resume errors. */
  entrypoint: SessionResumeErrorEntrypoint
  pending: boolean
  error?: string
  failure?: RelayFailure
  newConversationHref?: string
  progress?: RelayConnectionProgress
  presentationPhase?: 'waking' | 'connecting'
  slow?: boolean
  canRetry?: boolean
  onRetry: () => void
  onResumeIntent?: () => void
  draftOnly: boolean
}

function ChatShellContent({
  ahpSession,
  dormantQueuedMessagesReadOnly = false,
  displaySession,
  savedHistory,
  savedHistoryError,
  attachMenuSide,
  portalContainerName,
  emptyState,
  shipFeedbackEnabled,
  environmentSetupEnabled,
  establishedAhpSessionPending,
  establishedAhpSessionError,
  taskStartupTaskId,
  taskStartup,
  taskStartupCloneProgress,
  provisionalLaunchKind,
  submissionFailure,
  pendingInitialPrompt: hostPendingInitialPrompt,
  composerOnly,
  initialSubmission,
  initialPrompt,
  initialSubmissionMetadata,
  prepareInitialPrompt,
  initialSelectedSpace,
  initialApprovalMode,
  onInitialPromptDispatched,
  initialSubmissionOrigin = 'new',
  onSessionEstablished,
  activeAhpSession,
  chatShellRef,
  deleteDialog,
  disableAutoFocus,
  readOnly: readOnlyOverride,
  onContributedInstructions,
  sessionResume,
  historyOnlyContent,
}: {
  ahpSession?: AhpSessionIdentifiers
  dormantQueuedMessagesReadOnly?: boolean
  displaySession?: ChatDisplayIdentity
  savedHistory?: TaskAhpHistory
  savedHistoryError?: string
  attachMenuSide?: AnchorSide
  portalContainerName?: string
  emptyState?: EmptyStateMode
  shipFeedbackEnabled?: boolean
  environmentSetupEnabled?: boolean
  establishedAhpSessionPending?: boolean
  establishedAhpSessionError?: string
  taskStartupTaskId?: string
  taskStartup?: TaskStartup
  taskStartupCloneProgress?: number
  provisionalLaunchKind?: TaskStartupLaunch['kind']
  submissionFailure?: SubmissionFailure
  pendingInitialPrompt?: PreparedPrompt
  composerOnly?: boolean
  initialSubmission?: EstablishedAhpInitialSubmissionCoordinator
  initialPrompt?: PreparedPrompt
  initialSubmissionMetadata?: AhpSubmissionMetadata
  prepareInitialPrompt?: FirstTurnHandoff['preparePrompt']
  initialSelectedSpace?: AhpSpaceSelection
  initialApprovalMode?: SessionApprovalMode
  onInitialPromptDispatched?: () => void
  initialSubmissionOrigin?: 'new' | 'recovery'
  onSessionEstablished?: (session: EstablishedAhpSession) => void
  activeAhpSession?: AhpSessionIdentifiers
  chatShellRef: RefObject<HTMLElement | null>
  deleteDialog: ReactNode
  disableAutoFocus?: boolean
  /** Forces a non-composable shell, e.g. while the host's chat quota is exhausted. */
  readOnly?: boolean
  sessionResume?: SessionResumeState
  historyOnlyContent?: ReactNode
  onContributedInstructions: (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => void
}) {
  const {state: providerState, errorMessage} = useNativeAhpTimelineState()
  const historyOnly = historyOnlyContent != null
  const {state: persistedState} = useNativeAhpTimelineFallbackState()
  const contributedInstructions = useOptionalContributedInstructionSnapshot()
  const establishedSession = useOptionalEstablishedAhpSession()
  const changesetTaskId = establishedSession?.identity.taskId ?? displaySession?.taskId
  const changesetSessionId = establishedSession?.identity.sessionId ?? displaySession?.sessionId
  const establishedView = useOptionalEstablishedAhpSessionViewState()
  const [isDiffOpen, setIsDiffOpen] = useState(false)
  const {
    state: optimisticInitialState,
    hasTurns: hasOptimisticRecovery,
    hasActiveTurn: hasActiveOptimisticRecoveryTurn,
    turnCount: optimisticRecoveryTurnCount,
    onStarted: handleInitialSubmissionStarted,
    onStopped: handleInitialSubmissionStopped,
    clear: clearOptimisticRecovery,
  } = useOptimisticRecoveryTurns(providerState, hostPendingInitialPrompt)
  const canonicalProviderState = establishedSession ? establishedView.state : (optimisticInitialState ?? providerState)
  const chatEntitlement = useChatEntitlementOnTurnSettlement(canonicalProviderState?.activeTurn !== undefined)
  const canonicalProviderErrorMessage = establishedSession ? establishedView.error : errorMessage
  const canonicalErrorMessage = canonicalProviderErrorMessage ?? establishedAhpSessionError
  const runtimeStatus = useRuntimeStatus()
  const {selectedMode, onUpdateMode} = useModePicker()
  const independentModeControlsEnabled = isFeatureEnabled('copilot_mission_control_independent_mode_controls')
  const approvalMutation = useEstablishedApprovalModeMutation()
  const applyPermissionCommand = usePermissionCommandHandler()
  const repositoryCommandTaskId = establishedSession?.identity.taskId ?? activeAhpSession?.taskId
  const queryClient = useQueryClient()
  // A task's repository never changes, so it is looked up once per task, and only
  // when a repository command is sent. A conversation that has not created its
  // task yet cannot say whether it will have a repository, so its commands are
  // left to the host.
  const resolveTaskHasRepository = useCallback(
    async () =>
      repositoryCommandTaskId
        ? queryClient.fetchQuery({
            queryKey: ['agent-task-has-repository', repositoryCommandTaskId],
            queryFn: async ({signal}) => taskHasRepository(await fetchTask({taskId: repositoryCommandTaskId, signal})),
            staleTime: Infinity,
            retry: false,
          })
        : undefined,
    [queryClient, repositoryCommandTaskId],
  )
  const reportStaffCommandFeedback = useStaffDebugFeedbackToast()
  const mode = selectedMode ?? DEFAULT_AGENT_MODE
  const provisioningKind = useProvisioningKind()
  const effectiveRuntimeStatus = hasOptimisticRecovery ? 'provisioning' : runtimeStatus
  const effectiveProvisioningKind = hasOptimisticRecovery
    ? initialSubmissionOrigin === 'new'
      ? 'initial'
      : 'resume'
    : provisioningKind
  const hasConversationContent =
    Boolean(
      canonicalProviderState && ((canonicalProviderState.turns?.length ?? 0) > 0 || canonicalProviderState.activeTurn),
    ) || Boolean(savedHistory?.length)
  const isConversationEmpty = !activeAhpSession && !hasConversationContent
  const shouldShowEmptyState = Boolean(
    emptyState && isConversationEmpty && runtimeStatus === 'idle' && !canonicalErrorMessage,
  )
  const environmentSetupEligible = useEnvironmentSetupVisibility({
    enabled: environmentSetupEnabled,
    runtimeStatus: effectiveRuntimeStatus,
    activeAhpSession,
    provisioningKind: effectiveProvisioningKind,
  })
  const showEnvironmentSetup = environmentSetupEligible && !canonicalErrorMessage
  useSessionLoadingSource(
    canonicalErrorMessage
      ? {kind: 'preparation', state: 'error', replacesLoading: false}
      : {
          kind: 'preparation',
          state: showEnvironmentSetup ? 'pending' : 'idle',
          launchKind:
            showEnvironmentSetup &&
            !sessionResume &&
            (!taskStartupTaskId || (taskStartup?.status === 'pending' && taskStartup.phase === 'connecting'))
              ? taskStartup?.status === 'pending' && taskStartup.phase === 'connecting'
                ? taskStartup.launch?.kind
                : provisionalLaunchKind
              : undefined,
        },
    {enabled: true},
  )
  const resumeDraftOnly = Boolean(sessionResume?.draftOnly)
  const readOnly =
    Boolean(readOnlyOverride) ||
    (!resumeDraftOnly && Boolean(establishedAhpSessionError)) ||
    (establishedSession ? establishedView.passive : false)
  const state = canonicalProviderState
  const contentRefOrigin = useMemo(
    () =>
      establishedSession
        ? {
            environmentId: establishedSession.identity.environmentId,
            sessionId: establishedSession.identity.sessionId,
          }
        : undefined,
    [establishedSession],
  )
  const artifacts = useMemo(() => deriveArtifactsFromChatState(state, {contentRefOrigin}), [contentRefOrigin, state])
  const persistedSessionId = displaySession?.sessionId ?? activeAhpSession?.sessionId
  // A partial tail omits older artifacts, so their absence is not a removal and
  // must not close their open tabs. No state at all is likewise unsettled.
  const historyIsSettled = state !== undefined && state.turnsNextCursor === undefined
  const artifactWritesSettled = historyIsSettled && state?.activeTurn === undefined
  const previousShouldShowEmptyStateRef = useRef(shouldShowEmptyState)
  const environmentSetupAnnouncedRef = useRef(false)
  // Empty-state autofocus only targets the normal composer controls.
  const findComposerControl = useCallback(() => {
    const chatShell = chatShellRef.current
    return (
      chatShell?.querySelector<HTMLElement>('[data-testid="chat-composer-input"]') ??
      chatShell?.querySelector<HTMLElement>('[data-testid="chat-composer-confirmation"] button:not([disabled])') ??
      null
    )
  }, [chatShellRef])
  const focusComposerControl = useCallback(() => {
    findComposerControl()?.focus()
  }, [findComposerControl])
  // An `ask_user` takeover replaces the composer with its prompt, so that prompt
  // is the composer for as long as it is up. Only the artifacts fallback looks
  // there, without changing the empty-state autofocus.
  const findArtifactsFallbackFocus = useCallback(
    () =>
      findComposerControl() ??
      chatShellRef.current?.querySelector<HTMLElement>(
        '[data-testid="chat-composer-ask-user"] :is(input, textarea, button):not([disabled])',
      ) ??
      null,
    [chatShellRef, findComposerControl],
  )
  const {applyShareCommand, feedback: shareCommandFeedback} = useShareCommandHandler({
    taskId: establishedSession?.identity.taskId ?? activeAhpSession?.taskId,
    onDismissFeedback: () => findArtifactsFallbackFocus()?.focus(),
  })
  const {guardRepositoryCommand, feedback: repositoryCommandFeedback} = useRepositoryCommandGuard({
    resolveHasRepository: resolveTaskHasRepository,
    taskId: repositoryCommandTaskId,
    onDismissFeedback: () => findArtifactsFallbackFocus()?.focus(),
  })
  const timelineAutoScrollRef = useRef<TimelineAutoScrollHandle>(null)
  const composerRef = useRef<ComposerHandle>(null)
  const resumeNoticeId = useId()
  const pageLoadingPresentation = useSessionLoadingPresentation()
  const showResumeNotice = Boolean(sessionResume && (sessionResume.error || sessionResume.failure))
  const onResumeRetry = sessionResume?.onRetry
  const resumeFocusReturn = useEstablishedAhpComposerFocusReturn(showResumeNotice ? 'connection' : undefined, () =>
    findComposerControl()?.focus(),
  )
  const handleResumeRetry = useCallback(() => {
    onResumeRetry?.()
    requestAnimationFrame(focusComposerControl)
  }, [focusComposerControl, onResumeRetry])
  // `FileAttachment` wraps the whole panel and handles bubbled paste, drop and
  // change events, so a read-only composer would still accept files through it.
  const composeDisabled = readOnly || (establishedAhpSessionPending && !hasOptimisticRecovery && !resumeDraftOnly)
  const handleAcceptDroppedFiles = useCallback(
    (attachments: Attachment[]) => {
      if (composeDisabled) return
      composerRef.current?.addFiles(attachments.map(a => a.file))
    },
    [composeDisabled],
  )
  const scrollTimelineToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      timelineAutoScrollRef.current?.scrollToBottom('smooth')
    })
  }, [])
  const handleSessionEstablished = useCallback(
    (session: EstablishedAhpSession) => {
      if (!onSessionEstablished) return
      timelineAutoScrollRef.current?.preserveOffsetForNextContentPrepend()
      clearOptimisticRecovery()
      onSessionEstablished(session)
    },
    [clearOptimisticRecovery, onSessionEstablished],
  )
  useEffect(() => {
    if (hasOptimisticRecovery) scrollTimelineToBottom()
  }, [hasOptimisticRecovery, optimisticRecoveryTurnCount, scrollTimelineToBottom])
  useEffect(() => {
    if (previousShouldShowEmptyStateRef.current && !shouldShowEmptyState && !disableAutoFocus) {
      requestAnimationFrame(focusComposerControl)
    }
    previousShouldShowEmptyStateRef.current = shouldShowEmptyState
  }, [disableAutoFocus, focusComposerControl, shouldShowEmptyState])

  useEffect(() => {
    if (effectiveRuntimeStatus !== 'provisioning') {
      if (environmentSetupAnnouncedRef.current && !canonicalErrorMessage && effectiveRuntimeStatus !== 'error') {
        announce(ENVIRONMENT_READY_ANNOUNCEMENT)
      }
      environmentSetupAnnouncedRef.current = false
      return
    }
    if (!showEnvironmentSetup || environmentSetupAnnouncedRef.current) return
    environmentSetupAnnouncedRef.current = true
  }, [canonicalErrorMessage, effectiveRuntimeStatus, showEnvironmentSetup])

  return (
    <>
      {deleteDialog}
      <div className={styles.Body}>
        <FileAttachment
          onAccept={handleAcceptDroppedFiles}
          preventAccidentalDrops
          className={styles.ConversationPanel}
          // Scope FileAttachment's built-in file-input `onChange` handler to a
          // sentinel id no real input uses. The composer already owns its own
          // hidden file input; without this, its change events would bubble to
          // FileAttachment and double-attach the selected files.
          inputId="chat-shell-file-attachment-disabled"
          // Preserve section-level a11y semantics on the wrapping element. A
          // composer-only surface hosts no conversation, so announcing an empty
          // "Conversation" landmark around just the input would describe
          // something that isn't there.
          {...(composerOnly ? {} : ({role: 'region', 'aria-label': 'Conversation'} as const))}
        >
          {shipFeedbackEnabled && (
            <ShipFeedbackBanner inline={composerOnly} treatment="private-preview" alignment="end" />
          )}
          {composerOnly ? null : !shouldShowEmptyState || pageLoadingPresentation ? (
            <div className={styles.ConversationBody}>
              <Timeline
                ahpSession={ahpSession}
                displaySession={displaySession}
                stateOverride={taskStartupTaskId ? undefined : optimisticInitialState}
                savedHistory={savedHistory}
                savedHistoryError={savedHistoryError}
                autoScrollRef={timelineAutoScrollRef}
                afterContent={
                  <SessionLoadingIndicator
                    preparationVisual={showEnvironmentSetup ? <EnvironmentSetupLazy visualOnly /> : undefined}
                  />
                }
                suppressProgressMessage={showEnvironmentSetup && !taskStartupTaskId}
                establishedAhpSessionPending={sessionResume ? undefined : establishedAhpSessionPending}
                establishedAhpSessionError={sessionResume ? undefined : establishedAhpSessionError}
                taskStartupTaskId={taskStartupTaskId}
                taskStartup={taskStartup}
                taskStartupCloneProgress={taskStartupCloneProgress}
                savedHistoryEnabled={sessionResume !== undefined || savedHistory !== undefined}
                loadingPresentation="page"
                queueReadOnly={resumeDraftOnly}
                onContributedInstructions={onContributedInstructions}
              />
            </div>
          ) : null}
          {establishedSession ? (
            <EstablishedTaskInputActions
              artifacts={artifacts}
              sessionId={establishedSession.identity.sessionId}
              persistedState={persistedState}
              historyIsSettled={historyIsSettled}
              artifactWritesSettled={artifactWritesSettled}
              getArtifactsFallbackFocus={findArtifactsFallbackFocus}
              onViewDiff={() => setIsDiffOpen(true)}
            />
          ) : persistedSessionId ? (
            <PersistedTaskInputActions
              artifacts={artifacts}
              sessionId={persistedSessionId}
              persistedState={persistedState}
              historyIsSettled={historyIsSettled}
              artifactWritesSettled={artifactWritesSettled}
              getArtifactsFallbackFocus={findArtifactsFallbackFocus}
            />
          ) : (
            <Artifacts
              artifacts={artifacts}
              historyIsSettled={historyIsSettled}
              artifactWritesSettled={artifactWritesSettled}
              getFallbackFocusTarget={findArtifactsFallbackFocus}
            />
          )}
          {!establishedSession && !composerOnly && changesetTaskId && (
            <ChatChangesetActions
              key={changesetSessionId}
              taskId={changesetTaskId}
              saved={savedHistory?.find(saved => saved.sessionId === changesetSessionId)}
            />
          )}
          {establishedSession ? (
            <DiffOverlay
              isOpen={isDiffOpen}
              onClose={() => setIsDiffOpen(false)}
              repoNwo=""
              taskId={establishedSession.identity.taskId ?? ''}
              baseRef=""
              headRef=""
            />
          ) : null}
          {showResumeNotice && sessionResume ? (
            <div {...resumeFocusReturn}>
              <SessionResumeNotice
                id={resumeNoticeId}
                errorTelemetry={{
                  entrypoint: sessionResume.entrypoint,
                  taskId: activeAhpSession?.taskId ?? displaySession?.taskId,
                  sessionId: activeAhpSession?.sessionId ?? displaySession?.sessionId,
                  environmentId: activeAhpSession?.environmentId ?? displaySession?.environmentId,
                }}
                pending={sessionResume.pending}
                error={sessionResume.error}
                failure={sessionResume.failure}
                newConversationHref={sessionResume.newConversationHref}
                progress={sessionResume.progress}
                presentationPhase={sessionResume.presentationPhase}
                slow={sessionResume.slow}
                canRetry={sessionResume.canRetry}
                onRetry={handleResumeRetry}
              />
            </div>
          ) : null}
          {historyOnlyContent}
          {shareCommandFeedback}
          {repositoryCommandFeedback}
          <AhpSessionComposer
            ahpSession={ahpSession}
            state={state}
            contributedInstructions={contributedInstructions}
            queuedMessages={state?.queuedMessages}
            queueReadOnly={dormantQueuedMessagesReadOnly && !activeAhpSession}
            suppressQueuedMessages
            autoTierEnabled
            initialSubmission={initialSubmission}
            initialPrompt={initialPrompt}
            initialSubmissionMetadata={initialSubmissionMetadata}
            prepareInitialPrompt={prepareInitialPrompt}
            initialSubmissionOrigin={initialSubmissionOrigin}
            initialSelectedSpace={initialSelectedSpace}
            onInitialPromptDispatched={onInitialPromptDispatched}
            onInitialSubmissionStarted={handleInitialSubmissionStarted}
            onInitialSubmissionStopped={handleInitialSubmissionStopped}
            onInitialSubmissionSettled={clearOptimisticRecovery}
            onSessionEstablished={handleSessionEstablished}
            onSubmitSuccess={scrollTimelineToBottom}
            onResumeIntent={sessionResume?.onResumeIntent}
            submissionFailure={submissionFailure}
            sessionConfiguration={{
              mode,
              approvalMode: initialApprovalMode,
              modePicker: (
                <CLIModePicker
                  selectedMode={mode}
                  onModeChange={onUpdateMode}
                  selectedApprovalMode={independentModeControlsEnabled ? establishedView.approvalMode : undefined}
                  effectiveApprovalMode={
                    independentModeControlsEnabled ? establishedView.effectiveApprovalMode : undefined
                  }
                  availableApprovalModes={
                    independentModeControlsEnabled ? establishedView.availableApprovalModes : undefined
                  }
                  onApprovalModeChange={
                    independentModeControlsEnabled && !composeDisabled && !resumeDraftOnly
                      ? approvalMutation.onApprovalModeChange
                      : undefined
                  }
                  approvalModeMutable={establishedView.approvalModeMutable}
                  approvalModePending={approvalMutation.isPending}
                  approvalModeError={approvalMutation.error}
                  disabled={resumeDraftOnly || composeDisabled || approvalMutation.isPending}
                  portalContainerName={portalContainerName}
                />
              ),
              applyPermissionCommand,
              applyShareCommand,
              guardRepositoryCommand,
            }}
            reportStaffCommandFeedback={reportStaffCommandFeedback}
            attachMenuSide={attachMenuSide}
            readOnly={composeDisabled || historyOnly}
            chatEntitlement={composeDisabled || historyOnly ? undefined : chatEntitlement}
            draftOnly={resumeDraftOnly || historyOnly}
            hidden={historyOnly}
            composerDescriptionId={
              showResumeNotice ? resumeNoticeId : composerOnly ? undefined : pageLoadingPresentation?.descriptionId
            }
            disabled={hasActiveOptimisticRecoveryTurn}
            portalContainerName={portalContainerName}
            handleRef={composerRef}
            emptyState={shouldShowEmptyState ? emptyState : undefined}
            disableAutoFocus={disableAutoFocus}
          />
          {!historyOnly && !composeDisabled && (
            <div className={styles.DragOverlay} data-testid="chat-composer-drop-overlay" aria-hidden="true">
              <UploadIcon size={24} />
              <span>Drop files to attach</span>
            </div>
          )}
        </FileAttachment>
      </div>
    </>
  )
}

function ChatShellHeader({
  ahpSession,
  displaySession,
  activeAhpSession,
  canDeleteConversation,
  headerRef,
  openDeleteDialog,
}: {
  ahpSession?: AhpSessionIdentifiers
  displaySession?: ChatDisplayIdentity
  activeAhpSession?: AhpSessionIdentifiers
  canDeleteConversation: boolean
  headerRef: RefObject<HTMLDivElement | null>
  openDeleteDialog: () => void
}) {
  const {title, exportConversation} = useConversationHeaderState(ahpSession ?? displaySession)

  return (
    <div ref={headerRef} className={styles.HeaderContent}>
      <QuickChatHeader
        title={title}
        onExport={exportConversation}
        isProvisioned={Boolean(activeAhpSession)}
        onDelete={canDeleteConversation ? openDeleteDialog : undefined}
      />
    </div>
  )
}

function ChatShellRegion({
  chatShellRef,
  children,
  header,
  hideHeader,
  composerOnly,
}: {
  chatShellRef: RefObject<HTMLElement | null>
  children: ReactNode
  header?: ReactNode
  hideHeader?: boolean
  composerOnly?: boolean
}) {
  return (
    <section
      ref={chatShellRef}
      aria-labelledby={hideHeader ? undefined : QUICK_CHAT_HEADING_ID}
      aria-label={hideHeader ? 'Chat' : undefined}
      className={styles.ChatApp}
      data-composer-only={composerOnly ? 'true' : undefined}
    >
      {header}
      {children}
    </section>
  )
}

export function ChatShell({
  ahpSession,
  dormantQueuedMessagesReadOnly,
  displaySession,
  savedHistory,
  savedHistoryError,
  attachMenuSide,
  hideHeader,
  portalContainerName,
  emptyState,
  conversationDeletion,
  shipFeedbackEnabled,
  environmentSetupEnabled,
  establishedAhpSessionPending,
  establishedAhpSessionError,
  taskStartupTaskId,
  taskStartup,
  taskStartupCloneProgress,
  provisionalLaunchKind,
  submissionFailure,
  pendingInitialPrompt,
  composerOnly,
  initialSubmission,
  initialPrompt,
  initialSubmissionMetadata,
  prepareInitialPrompt,
  initialSelectedSpace,
  initialApprovalMode,
  onInitialPromptDispatched,
  initialSubmissionOrigin = 'new',
  onSessionEstablished,
  renderLayout,
  disableAutoFocus,
  readOnly,
  sessionResume,
  historyOnlyContent,
  historyLoading,
}: {
  ahpSession?: AhpSessionIdentifiers
  dormantQueuedMessagesReadOnly?: boolean
  displaySession?: ChatDisplayIdentity
  savedHistory?: TaskAhpHistory
  savedHistoryError?: string
  attachMenuSide?: AnchorSide
  hideHeader?: boolean
  portalContainerName?: string
  /**
   * Which empty-state surface to render before the first turn: `'embedded'`
   * for the compact panel (contextual suggestion pills) or `'immersive'` for
   * the centered home layout (Copilot animation, composer, icebreakers).
   * Undefined renders no empty state.
   */
  emptyState?: EmptyStateMode
  conversationDeletion?: ConversationDeletion
  /**
   * When true (the surrounding surface's experience flag is enabled), the staff
   * ship-feedback banner may render. The banner additionally gates on staff status
   * and `one_copilot_chat` without the UI opt-out.
   */
  shipFeedbackEnabled?: boolean
  environmentSetupEnabled?: boolean
  establishedAhpSessionPending?: boolean
  establishedAhpSessionError?: string
  taskStartupTaskId?: string
  taskStartup?: TaskStartup
  taskStartupCloneProgress?: number
  provisionalLaunchKind?: TaskStartupLaunch['kind']
  /**
   * A first turn dispatched outside the composer (one composed on another page
   * and replayed here) that failed. Surfaced on the composer, with the composed
   * text restored, because no other part of this surface reports it.
   */
  submissionFailure?: SubmissionFailure
  /** A host-dispatched first turn to show while it provisions. */
  pendingInitialPrompt?: PreparedPrompt
  /**
   * Renders the composer without the conversation region. An entry point starts a
   * conversation but never hosts one, and an empty timeline still claims the
   * space a conversation would need (`flex: 1`) — a gap above the input.
   */
  composerOnly?: boolean
  initialSubmission?: EstablishedAhpInitialSubmissionCoordinator
  initialPrompt?: PreparedPrompt
  initialSubmissionMetadata?: AhpSubmissionMetadata
  prepareInitialPrompt?: FirstTurnHandoff['preparePrompt']
  initialSelectedSpace?: AhpSpaceSelection
  /** Applies a new composer's approval choice before its first turn is dispatched. */
  initialApprovalMode?: SessionApprovalMode
  onInitialPromptDispatched?: () => void
  initialSubmissionOrigin?: 'new' | 'recovery'
  onSessionEstablished?: (session: EstablishedAhpSession) => void
  renderLayout?: ChatShellLayoutRenderer
  /** Forces a non-composable shell, e.g. while the host's chat quota is exhausted. */
  readOnly?: boolean
  sessionResume?: SessionResumeState
  historyOnlyContent?: ReactNode
  historyLoading?: boolean
  disableAutoFocus?: boolean
}) {
  const establishedSession = useOptionalEstablishedAhpSession()
  const runtimeAhpSession = useNativeAhpSession()
  const establishedIdentity = establishedSession?.identity
  const establishedAhpSession =
    establishedIdentity?.taskId && establishedIdentity.environmentId && establishedIdentity.sessionId
      ? {
          taskId: establishedIdentity.taskId,
          environmentId: establishedIdentity.environmentId,
          sessionId: establishedIdentity.sessionId,
          ...(establishedIdentity.channel ? {channel: establishedIdentity.channel} : {}),
        }
      : undefined
  const activeAhpSession = establishedAhpSession ?? ahpSession ?? runtimeAhpSession
  const deleteTaskId = activeAhpSession?.taskId ?? displaySession?.taskId
  const chatShellRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [contributedInstruction, setContributedInstruction] = useState<ContributedInstructionMetadata>()
  const [isPersonalInstructionsDialogOpen, setIsPersonalInstructionsDialogOpen] = useState(false)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const appliedInstructionsTriggerRef = useRef<HTMLElement | null>(null)
  const hasCopilotChatManager = useHasCopilotChatManager()
  const hasCustomInstructionsProvider = useHasCustomInstructionsProvider()
  const personalInstructionsEditor = useOptionalPersonalInstructionsEditor()
  const contributedInstructionSnapshot = useOptionalContributedInstructionSnapshot()
  const legacyContributedInstructionSnapshot = useLegacyContributedInstructionSnapshot()
  const currentInstructions = hasCustomInstructionsProvider
    ? contributedInstructionSnapshot
    : legacyContributedInstructionSnapshot
  const canDeleteConversation = Boolean(deleteTaskId) && Boolean(conversationDeletion)
  const openDeleteDialog = useCallback(() => {
    returnFocusRef.current =
      headerRef.current?.querySelector<HTMLElement>('[data-testid="conversation-header-menu-button"]') ?? null
    setContributedInstruction(undefined)
    setIsPersonalInstructionsDialogOpen(false)
    setIsDeleteDialogOpen(true)
  }, [])
  const closeDeleteDialog = useCallback(() => setIsDeleteDialogOpen(false), [])
  const openAppliedInstructions = useCallback(
    (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => {
      appliedInstructionsTriggerRef.current = trigger
      setIsDeleteDialogOpen(false)
      const instruction = instructions[0]
      if (instruction?.kind === 'personal' && (hasCopilotChatManager || personalInstructionsEditor)) {
        setContributedInstruction(undefined)
        setIsPersonalInstructionsDialogOpen(true)
      } else {
        setIsPersonalInstructionsDialogOpen(false)
        setContributedInstruction(instruction)
      }
    },
    [hasCopilotChatManager, personalInstructionsEditor],
  )
  const closeAppliedInstructions = useCallback(() => setContributedInstruction(undefined), [])
  const closePersonalInstructions = useCallback(() => setIsPersonalInstructionsDialogOpen(false), [])
  const header = (
    <ChatShellHeader
      ahpSession={ahpSession}
      displaySession={displaySession}
      activeAhpSession={activeAhpSession}
      canDeleteConversation={canDeleteConversation}
      headerRef={headerRef}
      openDeleteDialog={openDeleteDialog}
    />
  )

  const deleteDialog =
    conversationDeletion && deleteTaskId ? (
      <DeleteConversationDialogContainer
        open={isDeleteDialogOpen}
        taskId={deleteTaskId}
        conversationDeletion={conversationDeletion}
        onClose={closeDeleteDialog}
        returnFocusRef={returnFocusRef}
      />
    ) : null
  const content = (
    <ChatShellContent
      ahpSession={ahpSession}
      dormantQueuedMessagesReadOnly={dormantQueuedMessagesReadOnly}
      displaySession={displaySession}
      savedHistory={savedHistory}
      savedHistoryError={savedHistoryError}
      activeAhpSession={activeAhpSession}
      attachMenuSide={attachMenuSide}
      portalContainerName={portalContainerName}
      emptyState={emptyState}
      shipFeedbackEnabled={shipFeedbackEnabled}
      environmentSetupEnabled={environmentSetupEnabled}
      establishedAhpSessionPending={establishedAhpSessionPending}
      establishedAhpSessionError={establishedAhpSessionError}
      taskStartupTaskId={taskStartupTaskId}
      taskStartup={taskStartup}
      taskStartupCloneProgress={taskStartupCloneProgress}
      provisionalLaunchKind={provisionalLaunchKind}
      submissionFailure={submissionFailure}
      pendingInitialPrompt={pendingInitialPrompt}
      composerOnly={composerOnly}
      initialSubmission={initialSubmission}
      initialPrompt={initialPrompt}
      initialSubmissionMetadata={initialSubmissionMetadata}
      prepareInitialPrompt={prepareInitialPrompt}
      initialSelectedSpace={initialSelectedSpace}
      initialApprovalMode={initialApprovalMode}
      onInitialPromptDispatched={onInitialPromptDispatched}
      initialSubmissionOrigin={initialSubmissionOrigin}
      onSessionEstablished={onSessionEstablished}
      chatShellRef={chatShellRef}
      deleteDialog={deleteDialog}
      disableAutoFocus={disableAutoFocus}
      readOnly={readOnly}
      sessionResume={sessionResume}
      historyOnlyContent={historyOnlyContent}
      onContributedInstructions={openAppliedInstructions}
    />
  )

  const appliedInstructionsDialog = contributedInstruction ? (
    <AppliedInstructionsDialog
      contributedInstruction={contributedInstruction}
      currentInstructions={currentInstructions}
      onDismiss={closeAppliedInstructions}
      returnFocusRef={appliedInstructionsTriggerRef}
    />
  ) : null
  const personalInstructionsDialog = isPersonalInstructionsDialogOpen ? (
    <PersonalInstructionsDialog
      onDismiss={closePersonalInstructions}
      returnFocusRef={appliedInstructionsTriggerRef}
      personalInstructions={personalInstructionsEditor?.personalInstructions}
      onPersonalInstructionsChange={personalInstructionsEditor?.updatePersonalInstructions}
    />
  ) : null

  const loadingSources: SessionLoadingSource[] = [
    establishedAhpSessionError || sessionResume?.error || sessionResume?.failure
      ? {kind: 'connection', state: 'error', replacesLoading: false}
      : {
          kind: 'connection',
          state: establishedAhpSessionPending || sessionResume?.pending ? 'pending' : 'idle',
          phase: sessionResume?.presentationPhase ?? (sessionResume?.pending ? 'connecting' : undefined),
        },
    savedHistoryError
      ? {kind: 'history', state: 'error', replacesLoading: false}
      : {kind: 'history', state: historyLoading ? 'pending' : 'idle'},
  ]
  const displayKey = displaySession?.taskId ?? ahpSession?.taskId ?? 'new-conversation'
  const shell = renderLayout ? (
    renderLayout({
      header: hideHeader ? null : header,
      content: (
        <ChatShellRegion chatShellRef={chatShellRef} hideHeader={hideHeader} composerOnly={composerOnly}>
          {content}
          {appliedInstructionsDialog}
          {personalInstructionsDialog}
        </ChatShellRegion>
      ),
    })
  ) : (
    <ChatShellRegion
      chatShellRef={chatShellRef}
      hideHeader={hideHeader}
      composerOnly={composerOnly}
      header={!hideHeader && <header className={styles.Header}>{header}</header>}
    >
      {content}
      {appliedInstructionsDialog}
      {personalInstructionsDialog}
    </ChatShellRegion>
  )

  return (
    <SessionLoadingBoundary
      displayKey={displayKey}
      visible
      sources={loadingSources}
      announcementTargetRef={chatShellRef}
    >
      {shell}
    </SessionLoadingBoundary>
  )
}
